/** @format */
import { useRouter } from 'next/router';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { setSearchParamsForSearchResults } from '@toucan/client/global/nextjs';

import { GridRepeater } from '@toucan/client/shared/components/grid-repeater';

import { useSearchParams } from '@toucan/client/features/search/components/model-search-results/hooks/use-search-params';
import {
    Filter,
    FilterOption,
} from '@toucan/client/features/search/components/model-search-results/types';

import { transformFiltersToSearchParams } from '../../helpers';
import { SearchFilter } from './components/search-filter';
import { styles } from './filter-group.styles';
import { SearchFilterGroupSkeleton } from './skeleton';
import { SearchFilterGroupProps } from './types';

export const DEFAULT_TEST_ID = 'search-filter-group';

export const StyledSearchFilterGroup = styled.div`
    ${styles}
`;

export const SearchFilterGroup: FunctionComponent<SearchFilterGroupProps> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    filters,
    onFilterOpen,
    isLoading,
    ...otherProps
}: SearchFilterGroupProps) => {
    const router = useRouter();
    const { selectedFilters } = useSearchParams();

    function handleUpdate(filter: Filter, selectedItems: FilterOption[]): void {
        const searchParams = transformFiltersToSearchParams(
            filter,
            selectedItems,
            selectedFilters
        );

        setSearchParamsForSearchResults(router, {
            ...searchParams,
            page: undefined,
        });
    }

    if (isLoading) {
        return <SearchFilterGroupSkeleton numFilters={10} />;
    }

    return (
        <StyledSearchFilterGroup data-testid={dataTestId} {...otherProps}>
            <GridRepeater
                gridColumns={5}
                gridGap="0.8rem"
                data-testid={`${dataTestId}__grid-repeater`}
            >
                {filters.map((filter) => (
                    <SearchFilter
                        key={filter.id}
                        onUpdate={handleUpdate}
                        onOpen={onFilterOpen}
                        data-testid={`${dataTestId}__search-filter`}
                        className="search-filter"
                        filter={filter}
                        id={filter.id}
                    />
                ))}
            </GridRepeater>
        </StyledSearchFilterGroup>
    );
};
